/**
 * The "Display" popover for the opportunity board (ADV-1361, spec §3) — the
 * Linear/Catalyst display-control surface. Composes only vendored shadcn
 * primitives (Popover, ToggleGroup, Select, Toggle, Switch, Separator, Label,
 * Button) on Adva tokens; no hand-rolled controls.
 *
 * Split by ADR-0038: shareable state (view, ordering, closed-window) routes
 * through `useOpportunityViewParams` (URL); per-user chrome (card fields,
 * density, empty columns) routes through `useOpportunityDisplayStore`
 * (persisted localStorage).
 *
 * Controls whose backing data/feature lands in a later slice render DISABLED
 * rather than fabricated — same precedent as the `—` aggregate placeholders:
 *   · Group-by Columns/Rows/Color  → single-axis v1; swimlanes + color-by later
 *   · Territory + Time-in-stage chips → §7 M1/M2 migrations + S6 card slots
 *   · Closed-deals window → control is live + shareable, but the server-side
 *     WHERE it drives lands in S7 (so it is wired-but-inert today, spec §6).
 */
import { SlidersHorizontal, LayoutGrid, List } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Toggle } from "@/components/ui/toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import {
  useOpportunityDisplayStore,
  type OpportunityDisplayProperties,
} from "@/stores/opportunity-display-store";
import {
  useOpportunityViewParams,
  type ClosedWindow,
} from "@/components/v2/opportunities/use-opportunity-view-params";

interface PropertyField {
  key: keyof OpportunityDisplayProperties;
  label: string;
  /** Disabled until its backing column + card slot land (§7 M1/M2, S6). */
  disabled?: boolean;
}

const PROPERTY_FIELDS: PropertyField[] = [
  { key: "amount", label: "Amount" },
  { key: "salesperson", label: "Salesperson" },
  { key: "territory", label: "Territory", disabled: true },
  { key: "timeInStage", label: "Time in stage", disabled: true },
  { key: "address", label: "Address" },
  { key: "created", label: "Created" },
];

const ORDERING_OPTIONS: { id: string; label: string; disabled?: boolean }[] = [
  { id: "lead_date", label: "Created" },
  { id: "proposal_value", label: "Amount" },
  { id: "stage_entered_at", label: "Time in stage", disabled: true },
];
const DEFAULT_ORDERING_ID = "lead_date";

const CLOSED_WINDOW_OPTIONS: { value: ClosedWindow; label: string }[] = [
  { value: "1d", label: "Past day" },
  { value: "1w", label: "Past week" },
  { value: "2w", label: "Past 2 weeks" },
  { value: "1m", label: "Past month" },
  { value: "3m", label: "Past 3 months" },
  { value: "all", label: "All time" },
];

const SECTION_LABEL = "typo-label text-[color:var(--color-text-lo)]";
const ROW_LABEL =
  "typo-label normal-case tracking-normal text-[color:var(--color-text-md)]";

/** Labeled control row: left label, right control. */
function ControlRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label htmlFor={htmlFor} className={ROW_LABEL}>
        {label}
      </Label>
      {children}
    </div>
  );
}

export function OpportunityDisplayPopover() {
  const {
    view,
    setView,
    ordering,
    setOrdering,
    closedWindow,
    setClosedWindow,
    subGroupBy,
    resetDisplayParams,
  } = useOpportunityViewParams();
  const {
    properties,
    density,
    showEmptyColumns,
    toggleProperty,
    setDensity,
    setShowEmptyColumns,
    reset,
  } = useOpportunityDisplayStore();

  // The popover's view toggle is the same single source of truth (URL) the
  // header switcher drives — Board ↔ List. Analytics leaves both items off.
  const toggleValue =
    view === "board" ? "board" : view === "table" ? "list" : "";

  function handleReset() {
    reset();
    resetDisplayParams();
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
          Display
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="flex flex-col gap-3">
          {/* (a) View toggle */}
          <ToggleGroup
            type="single"
            value={toggleValue}
            onValueChange={(next) => {
              if (next === "board") setView("board");
              else if (next === "list") setView("table");
            }}
            className="justify-start gap-1"
            aria-label="Board or list view"
          >
            <ToggleGroupItem value="board" className="h-8 gap-1.5 px-2.5">
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
              Board
            </ToggleGroupItem>
            <ToggleGroupItem value="list" className="h-8 gap-1.5 px-2.5">
              <List className="h-3.5 w-3.5" aria-hidden />
              List
            </ToggleGroupItem>
          </ToggleGroup>

          <Separator />

          {/* (b) Grouping + ordering. Columns/Rows/Color are single-axis v1 —
              the secondary axes (swimlanes, color-by) land in a later slice, so
              they render disabled. Ordering is live. */}
          <div className="flex flex-col gap-2">
            <p className={SECTION_LABEL}>Grouping</p>
            <ControlRow label="Columns">
              <Select value="stage" disabled>
                <SelectTrigger className="h-8 w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stage">Stage</SelectItem>
                </SelectContent>
              </Select>
            </ControlRow>
            <ControlRow label="Rows">
              <Select value={subGroupBy ?? "none"} disabled>
                <SelectTrigger className="h-8 w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="salesperson">Salesperson</SelectItem>
                  <SelectItem value="territory">Territory</SelectItem>
                </SelectContent>
              </Select>
            </ControlRow>
            <ControlRow label="Ordering">
              <Select
                value={ordering?.id ?? DEFAULT_ORDERING_ID}
                onValueChange={(id) => setOrdering({ id, desc: true })}
              >
                <SelectTrigger className="h-8 w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ORDERING_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.id}
                      value={opt.id}
                      disabled={opt.disabled}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ControlRow>
            <ControlRow label="Color">
              <Select value="stage" disabled>
                <SelectTrigger className="h-8 w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stage">Stage</SelectItem>
                </SelectContent>
              </Select>
            </ControlRow>
          </div>

          <Separator />

          {/* (c) Display properties — one chip per card field. */}
          <div className="flex flex-col gap-2">
            <p className={SECTION_LABEL}>Display properties</p>
            <div className="flex flex-wrap gap-1.5">
              {PROPERTY_FIELDS.map((field) => (
                <Toggle
                  key={field.key}
                  variant="outline"
                  size="sm"
                  pressed={properties[field.key]}
                  disabled={field.disabled}
                  onPressedChange={() => toggleProperty(field.key)}
                  aria-label={`Show ${field.label}`}
                >
                  {field.label}
                </Toggle>
              ))}
            </div>
          </div>

          <Separator />

          {/* (d) Options. */}
          <div className="flex flex-col gap-3">
            <ControlRow label="Compact" htmlFor="opp-display-compact">
              <Switch
                id="opp-display-compact"
                checked={density === "compact"}
                onCheckedChange={(on) =>
                  setDensity(on ? "compact" : "comfortable")
                }
              />
            </ControlRow>
            <ControlRow label="Show empty columns" htmlFor="opp-display-empty">
              <Switch
                id="opp-display-empty"
                checked={showEmptyColumns}
                onCheckedChange={setShowEmptyColumns}
              />
            </ControlRow>
          </div>

          <Separator />

          {/* (e) Closed-deals window. Live + shareable; the server-side WHERE
              it drives lands in S7 (wired-but-inert today, spec §6). */}
          <ControlRow label="Closed deals">
            <Select
              value={closedWindow}
              onValueChange={(next) => setClosedWindow(next as ClosedWindow)}
            >
              <SelectTrigger className="h-8 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CLOSED_WINDOW_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ControlRow>

          <Separator />

          {/* (f) Reset. */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={handleReset}
          >
            Reset to defaults
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
